import { useEffect, useRef } from "react";

type HankoAuthElementProps = {
  lang: string;
};

const PASSCODE_LENGTH = 6;

function isPasscodeInput(element: EventTarget | null): element is HTMLInputElement {
  return (
    element instanceof HTMLInputElement &&
    element.getAttribute("part")?.split(/\s+/).includes("passcode-input") === true
  );
}

function getPasscodeInputs(root: ShadowRoot): HTMLInputElement[] {
  return Array.from(root.querySelectorAll<HTMLInputElement>('input[part~="passcode-input"]'));
}

function isCompletePasscode(root: ShadowRoot): boolean {
  const inputs = getPasscodeInputs(root);
  return (
    inputs.length >= PASSCODE_LENGTH &&
    inputs.slice(0, PASSCODE_LENGTH).every((input) => input.value.trim().length > 0)
  );
}

function getPasscodeForm(root: ShadowRoot): HTMLFormElement | null {
  return getPasscodeInputs(root)[0]?.closest("form") ?? null;
}

function setGuardedSubmitDisabled(root: ShadowRoot, disabled: boolean) {
  const form = getPasscodeForm(root);
  const submitButton = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!submitButton) {
    return;
  }

  if (disabled) {
    submitButton.disabled = true;
    submitButton.setAttribute("data-agent-passcode-guard", "true");
    return;
  }

  if (submitButton.getAttribute("data-agent-passcode-guard") === "true") {
    submitButton.disabled = false;
    submitButton.removeAttribute("data-agent-passcode-guard");
  }
}

function stopEvent(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

export function installHankoPasscodeSubmitGuard(root: ShadowRoot): () => void {
  let autoSubmitInFlight = false;

  const syncButtonState = () => {
    if (!isCompletePasscode(root)) {
      autoSubmitInFlight = false;
    }
    setGuardedSubmitDisabled(root, autoSubmitInFlight);
  };

  const blockDuplicateSubmit = (event: Event) => {
    if (!autoSubmitInFlight || !isCompletePasscode(root)) {
      return;
    }
    stopEvent(event);
  };

  const blockDuplicateButtonActivation = (event: Event) => {
    const form = getPasscodeForm(root);
    const target = event.target instanceof Element ? event.target : null;
    if (!form || !target || !form.contains(target)) {
      return;
    }
    blockDuplicateSubmit(event);
  };

  const onInput = (event: Event) => {
    if (!isPasscodeInput(event.target)) {
      return;
    }

    window.setTimeout(() => {
      autoSubmitInFlight = isCompletePasscode(root);
      syncButtonState();
    }, 0);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      blockDuplicateButtonActivation(event);
    }
  };

  const observer = new MutationObserver(syncButtonState);

  root.addEventListener("input", onInput, true);
  root.addEventListener("submit", blockDuplicateSubmit, true);
  root.addEventListener("click", blockDuplicateButtonActivation, true);
  root.addEventListener("keydown", onKeyDown, true);
  observer.observe(root, { childList: true, subtree: true });
  syncButtonState();

  return () => {
    root.removeEventListener("input", onInput, true);
    root.removeEventListener("submit", blockDuplicateSubmit, true);
    root.removeEventListener("click", blockDuplicateButtonActivation, true);
    root.removeEventListener("keydown", onKeyDown, true);
    observer.disconnect();
    setGuardedSubmitDisabled(root, false);
  };
}

export default function HankoAuthElement({ lang }: HankoAuthElementProps) {
  const hostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cleanupGuard: (() => void) | undefined;
    const interval = window.setInterval(() => {
      if (cleanupGuard || !hostRef.current?.shadowRoot) {
        return;
      }
      cleanupGuard = installHankoPasscodeSubmitGuard(hostRef.current.shadowRoot);
    }, 100);

    return () => {
      window.clearInterval(interval);
      cleanupGuard?.();
    };
  }, []);

  return (
    <hanko-auth
      lang={lang}
      ref={(element) => {
        hostRef.current = element as HTMLElement | null;
      }}
    />
  );
}
